import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface GiftCardAvailableService {
  id: string;
  service_id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  services: {
    id: string;
    name: string;
    description: string | null;
    duration: string;
    price: string;
    category_id: string;
  };
}

// Hook to get available services for gift cards
export const useGiftCardAvailableServices = () => {
  return useQuery({
    queryKey: ["gift-card-available-services"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gift_card_available_services")
        .select(`
          *,
          services (
            id,
            name,
            description,
            duration,
            price,
            category_id
          )
        `)
        .eq("is_active", true);

      if (error) throw error;
      return data as GiftCardAvailableService[];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

// Hook to get all services with their availability status (for admin settings)
export const useAllServicesWithAvailability = () => {
  return useQuery({
    queryKey: ["all-services-with-availability"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("services")
        .select(`
          *,
          gift_card_available_services (
            id,
            is_active
          )
        `);

      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

// Hook to update service availability
export const useUpdateServiceAvailability = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ serviceId, isActive }: { serviceId: string; isActive: boolean }) => {
      console.log('🔧 Updating service availability:', { serviceId, isActive });
      
      // Check if entry exists
      const { data: existing, error: checkError } = await supabase
        .from("gift_card_available_services")
        .select("id")
        .eq("service_id", serviceId)
        .maybeSingle();

      if (checkError) {
        console.error('❌ Error checking existing entry:', checkError);
        throw checkError;
      }

      if (existing) {
        console.log('🔄 Updating existing entry:', existing.id);
        // Update existing entry
        const { error } = await supabase
          .from("gift_card_available_services")
          .update({ is_active: isActive })
          .eq("service_id", serviceId);

        if (error) {
          console.error('❌ Error updating entry:', error);
          throw error;
        }
      } else {
        console.log('➕ Creating new entry');
        // Insert new entry
        const { error } = await supabase
          .from("gift_card_available_services")
          .insert({ service_id: serviceId, is_active: isActive });

        if (error) {
          console.error('❌ Error inserting entry:', error);
          throw error;
        }
      }
      
      console.log('✅ Service availability updated successfully');
    },
    onError: (error) => {
      console.error('❌ Mutation failed:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour la configuration.",
        variant: "destructive",
      });
    },
    onSettled: async () => {
      console.log('🔄 Invalidating queries after mutation...');
      // Add a small delay to ensure database consistency
      await new Promise(resolve => setTimeout(resolve, 100));
      
      await queryClient.invalidateQueries({ queryKey: ["all-services-with-availability"] });
      await queryClient.invalidateQueries({ queryKey: ["gift-card-available-services"] });
      
      toast({
        title: "Configuration mise à jour",
        description: "La disponibilité du service a été modifiée.",
      });
    },
  });
};

// Hook to toggle all services availability
export const useToggleAllServices = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (isActive: boolean) => {
      console.log('🔧 Toggling all services:', isActive);
      
      // Get all services
      const { data: services, error: servicesError } = await supabase
        .from("services")
        .select("id");

      if (servicesError) {
        console.error('❌ Error fetching services:', servicesError);
        throw servicesError;
      }

      console.log(`📋 Found ${services.length} services to update`);

      // Upsert all services
      const upsertData = services.map(service => ({
        service_id: service.id,
        is_active: isActive,
      }));

      const { error } = await supabase
        .from("gift_card_available_services")
        .upsert(upsertData, { onConflict: "service_id" });

      if (error) {
        console.error('❌ Error upserting data:', error);
        throw error;
      }
      
      console.log('✅ All services updated successfully');
    },
    onError: (error) => {
      console.error('❌ Toggle all mutation failed:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour la configuration.",
        variant: "destructive",
      });
    },
    onSettled: async (_, error, isActive) => {
      console.log('🔄 Invalidating queries after toggle all...');
      // Add a small delay to ensure database consistency
      await new Promise(resolve => setTimeout(resolve, 100));
      
      await queryClient.invalidateQueries({ queryKey: ["all-services-with-availability"] });
      await queryClient.invalidateQueries({ queryKey: ["gift-card-available-services"] });
      
      if (!error) {
        toast({
          title: "Configuration mise à jour",
          description: `Tous les services ont été ${isActive ? "activés" : "désactivés"}.`,
        });
      }
    },
  });
};